import { useState, useEffect } from 'react'
import { HANDOFF_DRAG_MIME } from '@papan/types'
import { filesApi } from './index'
import { handoffApi, newHandoffId } from './handoff'

// ─── Offering a drag to apps (#605) ─────────────────────────────────
// A drive drag can be dropped onto an app (a PDApp or HTML app running in
// Papan, or on pd.app) — which then receives the CONTENT of what was dragged,
// never ids. The drag carries a one-time token (Papan's own drop targets read the
// drag's payload from `@papan/core-ui/ui/papanDrag`, never from the drag — #750),
// registered with the server as the person dragging; a drop redeems it once,
// only for them. `copyMove` so an app's copy-intent drop is accepted while every
// move target keeps working.

/** Call from every drive `dragstart` that drags files or folders. */
export function offerDragToApps(dataTransfer: DataTransfer, ids: string[]): void {
  if (ids.length === 0) return
  const token = newHandoffId()
  dataTransfer.setData(HANDOFF_DRAG_MIME, token)
  // A link drag (Alt held, #750) allows every effect so Alt held through the drop
  // still works; copyMove would refuse the LINK drop Windows asks for.
  dataTransfer.effectAllowed = dataTransfer.effectAllowed === 'all' ? 'all' : 'copyMove'
  handoffApi.registerDrag(token, ids)
}

// ─── Centralized forbidden-move check ────────────────────────────────
// A move is forbidden if any dragged folder would end up inside itself.
// That happens when a dragged folder IS the target or is an ANCESTOR
// of the target (moving parent into child = circular).

/**
 * Check if moving the dragged folders into targetId would be circular.
 *
 * @param dragFolderIds  IDs of folders being dragged
 * @param targetId       ID of the drop target folder
 * @param targetAncestorIds  Pre-fetched ancestor IDs of the target (from getBreadcrumbs)
 */
export function isCircularMove(
  dragFolderIds: ReadonlySet<string>,
  targetId: string,
  targetAncestorIds: ReadonlySet<string>,
): boolean {
  for (const dragId of dragFolderIds) {
    // Dropping a folder onto itself
    if (dragId === targetId) return true
    // Dropping a folder into one of its own descendants (ancestor of target = parent/grandparent)
    if (targetAncestorIds.has(dragId)) return true
  }
  return false
}

/**
 * React hook: pre-fetch ancestor IDs for a folder.
 * Returns a Set of all ancestor folder IDs (including the folder itself).
 * Used by any drop target to call isCircularMove().
 */
export function useFolderAncestors(folderId: string | null): ReadonlySet<string> {
  const [ancestorIds, setAncestorIds] = useState<ReadonlySet<string>>(new Set())
  useEffect(() => {
    if (!folderId) { setAncestorIds(new Set()); return }
    filesApi.getBreadcrumbs(folderId).then(crumbs => {
      setAncestorIds(new Set(crumbs.map(c => c.id)))
    }).catch(() => {})
  }, [folderId])
  return ancestorIds
}

// ─── Module-level drag state (same-tab fast path) ───────────────────
// Still useful for same-tab descendant tracking where we need more IDs
// than just the directly-dragged folders (e.g. all loaded descendants).

let activeDragFolderIds: ReadonlySet<string> = new Set()

/** Call from dragStart to register which folders are being dragged */
export function setActiveDragFolderIds(ids: Iterable<string>): void {
  activeDragFolderIds = new Set(ids)
}

/** Read during dragOver — same-tab only */
export function getActiveDragFolderIds(): ReadonlySet<string> {
  return activeDragFolderIds
}

/** Call from dragEnd to clear the active drag state */
export function clearActiveDrag(): void {
  activeDragFolderIds = new Set()
}

/**
 * Create a custom drag image element for Papan DnD.
 * Caller must append to body and schedule cleanup via requestAnimationFrame.
 */
export function createDragImage(files: { name: string }[]): HTMLDivElement {
  const count = files.length
  const dragEl = document.createElement('div')
  dragEl.className =
    'bg-zinc-800 border border-zinc-600 rounded-lg px-4 py-2 shadow-xl text-zinc-200 text-sm whitespace-nowrap'
  dragEl.textContent = count === 1 ? files[0].name : `${count} items`
  dragEl.style.position = 'absolute'
  dragEl.style.top = '-1000px'
  dragEl.style.left = '-1000px'
  return dragEl
}
